import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface Props {
  size?: number;
  withWordmark?: boolean;
  to?: string;
  className?: string;
  animate?: boolean;
}

/**
 * BrandMark — the ribbon "K" drawn as SVG so it gets the live gradient
 * (matches the logo PNG but renders crisply at any size and animates on mount).
 */
export function BrandMark({ size = 32, withWordmark = false, to = "/dashboard", animate = true, className }: Props) {
  const inner = (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <span className="relative inline-block" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
          <defs>
            <linearGradient id="krew-ribbon" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="hsl(var(--krew-lime))" />
              <stop offset="30%" stopColor="hsl(var(--krew-cyan))" />
              <stop offset="65%" stopColor="hsl(var(--krew-magenta))" />
              <stop offset="100%" stopColor="hsl(var(--krew-violet))" />
            </linearGradient>
          </defs>
          {/* Left vertical stroke of the K */}
          <motion.rect
            x="14" y="12" rx="14" ry="14" width="22" height="76"
            fill="none" stroke="url(#krew-ribbon)" strokeWidth="6"
            initial={animate ? { pathLength: 0, opacity: 0 } : false}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
          />
          {/* Diagonal "ribbon" of the K */}
          <motion.path
            d="M36 50 C 50 50, 60 30, 80 14"
            fill="none" stroke="url(#krew-ribbon)" strokeWidth="6" strokeLinecap="round"
            initial={animate ? { pathLength: 0 } : false}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
          />
          <motion.path
            d="M36 50 C 50 50, 60 70, 80 86"
            fill="none" stroke="url(#krew-ribbon)" strokeWidth="6" strokeLinecap="round"
            initial={animate ? { pathLength: 0 } : false}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
          />
        </svg>
      </span>
      {withWordmark && (
        <span className="font-display text-2xl font-semibold tracking-tight text-gradient-ribbon animate-ribbon-shift">
          krew
        </span>
      )}
    </span>
  );
  return to ? <Link to={to} className="inline-flex items-center">{inner}</Link> : inner;
}

export default BrandMark;